import React, { useState } from "react";
import type { AdminData } from "./AdminLayout";

interface Props {
  data: AdminData;
  updateData: (updater: (prev: AdminData) => AdminData) => void;
}

type ModalType =
  | "addSection"
  | "addSubmenu"
  | "editSectionLabel"
  | "editSubmenuLabel"
  | "addAddon"
  | "addInfo"
  | null;

const LOCATIONS = ["montreal", "delson"] as const;

const SectionsManager: React.FC<Props> = ({ data, updateData }) => {
  const [modal, setModal] = useState<ModalType>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedSubmenu, setSelectedSubmenu] = useState<string>("");
  const [formLabelEn, setFormLabelEn] = useState("");
  const [formLabelFr, setFormLabelFr] = useState("");
  const [formKey, setFormKey] = useState("");
  const [formOrder, setFormOrder] = useState(0);

  // Addon form
  const [addonName, setAddonName] = useState("");
  const [addonNameFr, setAddonNameFr] = useState("");
  const [addonPrice, setAddonPrice] = useState("");
  // Info form
  const [infoText, setInfoText] = useState("");
  const [infoTextFr, setInfoTextFr] = useState("");

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const getItemCount = (location: string, submenu: string) => {
    return data.menuItems.filter(
      (item) =>
        item.submenu === submenu &&
        (item.location === location || item.location === "both"),
    ).length;
  };

  // Add Section
  const openAddSection = (location: string) => {
    setSelectedLocation(location);
    setFormKey("");
    setFormLabelEn("");
    setFormLabelFr("");
    setFormOrder(0);
    setModal("addSection");
  };

  const handleAddSection = () => {
    if (!formKey || !formLabelEn || !formLabelFr) return;

    updateData((prev) => {
      const configs = JSON.parse(JSON.stringify(prev.menuConfigs));
      configs[selectedLocation][formKey] = {
        label: { en: formLabelEn, fr: formLabelFr },
        submenus: [],
        order: formOrder,
      };
      return { ...prev, menuConfigs: configs };
    });
    setModal(null);
  };

  // Add Submenu
  const openAddSubmenu = (location: string, section: string) => {
    setSelectedLocation(location);
    setSelectedSection(section);
    setFormKey("");
    setFormLabelEn("");
    setFormLabelFr("");
    setModal("addSubmenu");
  };

  const handleAddSubmenu = () => {
    if (!formKey || !formLabelEn || !formLabelFr) return;

    updateData((prev) => {
      const configs = JSON.parse(JSON.stringify(prev.menuConfigs));
      const submenus = [...configs[selectedLocation][selectedSection].submenus];
      submenus.push(formKey);
      configs[selectedLocation][selectedSection].submenus = submenus;

      const labels = { ...prev.submenuLabels };
      labels[formKey] = { en: formLabelEn, fr: formLabelFr };

      return { ...prev, menuConfigs: configs, submenuLabels: labels };
    });
    setModal(null);
  };

  // Edit Section Label
  const openEditSectionLabel = (
    location: string,
    section: string,
    labelEn: string,
    labelFr: string,
  ) => {
    setSelectedLocation(location);
    setSelectedSection(section);
    setFormLabelEn(labelEn);
    setFormLabelFr(labelFr);
    setModal("editSectionLabel");
  };

  const handleEditSectionLabel = () => {
    updateData((prev) => {
      const configs = JSON.parse(JSON.stringify(prev.menuConfigs));
      configs[selectedLocation][selectedSection].label = {
        en: formLabelEn,
        fr: formLabelFr,
      };
      return { ...prev, menuConfigs: configs };
    });
    setModal(null);
  };

  // Edit Submenu Label
  const openEditSubmenuLabel = (submenu: string) => {
    setSelectedSubmenu(submenu);
    const label = data.submenuLabels[submenu];
    setFormLabelEn(label?.en || "");
    setFormLabelFr(label?.fr || "");
    setModal("editSubmenuLabel");
  };

  const handleEditSubmenuLabel = () => {
    updateData((prev) => ({
      ...prev,
      submenuLabels: {
        ...prev.submenuLabels,
        [selectedSubmenu]: { en: formLabelEn, fr: formLabelFr },
      },
    }));
    setModal(null);
  };

  // Delete submenu
  const handleDeleteSubmenu = (
    location: string,
    section: string,
    submenu: string,
  ) => {
    const count = getItemCount(location, submenu);
    if (count > 0) {
      alert(
        `Cannot delete: ${count} menu items are in this submenu. Remove them first.`,
      );
      setDeleteConfirm(null);
      return;
    }

    updateData((prev) => {
      const configs = JSON.parse(JSON.stringify(prev.menuConfigs));
      const submenus = configs[location][section].submenus.filter(
        (s: string) => s !== submenu,
      );
      configs[location][section].submenus = submenus;
      return { ...prev, menuConfigs: configs };
    });
    setDeleteConfirm(null);
  };

  // Delete section
  const handleDeleteSection = (location: string, section: string) => {
    const config = data.menuConfigs[location as "montreal" | "delson"][section];
    let totalItems = 0;
    for (const sub of config.submenus) {
      totalItems += getItemCount(location, sub as string);
    }
    if (totalItems > 0) {
      alert(
        `Cannot delete: ${totalItems} menu items exist in this section's submenus.`,
      );
      setDeleteConfirm(null);
      return;
    }

    updateData((prev) => {
      const configs = JSON.parse(JSON.stringify(prev.menuConfigs));
      delete configs[location][section];
      return { ...prev, menuConfigs: configs };
    });
    setDeleteConfirm(null);
  };

  // Add-ons management
  const openAddAddon = (location: string, section: string, submenu: string) => {
    setSelectedLocation(location);
    setSelectedSection(section);
    setSelectedSubmenu(submenu);
    setAddonName("");
    setAddonNameFr("");
    setAddonPrice("");
    setModal("addAddon");
  };

  const handleAddAddon = () => {
    if (!addonName || !addonNameFr || !addonPrice) return;
    const key = `${selectedSection}-${selectedSubmenu}`;

    updateData((prev) => {
      const addOns = JSON.parse(JSON.stringify(prev.submenuAddOns));
      if (!addOns[selectedLocation][key]) {
        addOns[selectedLocation][key] = {
          submenu: selectedSubmenu,
          addOns: [],
        };
      }
      addOns[selectedLocation][key].addOns.push({
        name: addonName,
        nameFr: addonNameFr,
        price: parseFloat(addonPrice),
      });
      return { ...prev, submenuAddOns: addOns };
    });
    setModal(null);
  };

  // Info management
  const openAddInfo = (location: string, section: string, submenu: string) => {
    setSelectedLocation(location);
    setSelectedSection(section);
    setSelectedSubmenu(submenu);
    const key = `${section}-${submenu}`;
    const existing = data.submenuInfos[location as "montreal" | "delson"][key];
    setInfoText(existing?.info || "");
    setInfoTextFr(existing?.infoFr || "");
    setModal("addInfo");
  };

  const handleSaveInfo = () => {
    const key = `${selectedSection}-${selectedSubmenu}`;

    updateData((prev) => {
      const infos = JSON.parse(JSON.stringify(prev.submenuInfos));
      if (infoText && infoTextFr) {
        infos[selectedLocation][key] = {
          submenu: selectedSubmenu,
          info: infoText,
          infoFr: infoTextFr,
        };
      } else {
        delete infos[selectedLocation][key];
      }
      return { ...prev, submenuInfos: infos };
    });
    setModal(null);
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Menu Structure</h2>
      </div>

      {LOCATIONS.map((location) => {
        const sections = Object.entries(data.menuConfigs[location]).sort(
          ([, a], [, b]) => a.order - b.order,
        );

        return (
          <div key={location} className="admin-tree-location">
            <h3>
              {location.toUpperCase()}
              <button
                className="btn btn-primary btn-sm"
                style={{ marginLeft: "0.75rem" }}
                onClick={() => openAddSection(location)}
              >
                + Section
              </button>
            </h3>

            {sections.map(([sectionKey, config]) => (
              <div key={sectionKey} className="admin-tree-section">
                <div className="admin-tree-section-header">
                  <strong>
                    {config.label.en} / {config.label.fr}
                  </strong>
                  <span
                    className="admin-badge"
                    style={{ background: "#e5e7eb" }}
                  >
                    order: {config.order}
                  </span>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                      openEditSectionLabel(
                        location,
                        sectionKey,
                        config.label.en,
                        config.label.fr,
                      )
                    }
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => openAddSubmenu(location, sectionKey)}
                  >
                    + Submenu
                  </button>
                  {deleteConfirm === `section-${location}-${sectionKey}` ? (
                    <>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() =>
                          handleDeleteSection(location, sectionKey)
                        }
                      >
                        Confirm
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDeleteConfirm(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() =>
                        setDeleteConfirm(`section-${location}-${sectionKey}`)
                      }
                    >
                      Delete
                    </button>
                  )}
                </div>

                {config.submenus.map((submenu) => {
                  const sub = submenu as string;
                  const label = data.submenuLabels[sub];
                  const count = getItemCount(location, sub);
                  const deleteKey = `sub-${location}-${sectionKey}-${sub}`;

                  return (
                    <div key={sub} className="admin-tree-submenu">
                      <span>
                        {label?.en || sub} / {label?.fr || ""}
                      </span>
                      <span className="item-count">[{count} items]</span>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEditSubmenuLabel(sub)}
                      >
                        Label
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openAddAddon(location, sectionKey, sub)}
                      >
                        Add-ons
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openAddInfo(location, sectionKey, sub)}
                      >
                        Info
                      </button>
                      {deleteConfirm === deleteKey ? (
                        <>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() =>
                              handleDeleteSubmenu(location, sectionKey, sub)
                            }
                          >
                            Confirm
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => setDeleteConfirm(deleteKey)}
                        >
                          Del
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}

      {/* Modals */}
      {modal === "addSection" && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add Section to {selectedLocation}</h3>
            <div className="admin-field">
              <label>Section Key (kebab-case)</label>
              <input
                value={formKey}
                onChange={(e) => setFormKey(e.target.value)}
                placeholder="e.g. brunch"
              />
            </div>
            <div className="admin-field-row">
              <div className="admin-field">
                <label>Label (EN)</label>
                <input
                  value={formLabelEn}
                  onChange={(e) => setFormLabelEn(e.target.value)}
                />
              </div>
              <div className="admin-field">
                <label>Label (FR)</label>
                <input
                  value={formLabelFr}
                  onChange={(e) => setFormLabelFr(e.target.value)}
                />
              </div>
            </div>
            <div className="admin-field">
              <label>Order</label>
              <input
                type="number"
                value={formOrder}
                onChange={(e) => setFormOrder(parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="admin-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddSection}>
                Add Section
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "addSubmenu" && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              Add Submenu to {selectedLocation}/{selectedSection}
            </h3>
            <div className="admin-field">
              <label>Submenu Key (kebab-case)</label>
              <input
                value={formKey}
                onChange={(e) => setFormKey(e.target.value)}
                placeholder="e.g. delson-lunch-salads"
              />
            </div>
            <div className="admin-field-row">
              <div className="admin-field">
                <label>Label (EN)</label>
                <input
                  value={formLabelEn}
                  onChange={(e) => setFormLabelEn(e.target.value)}
                />
              </div>
              <div className="admin-field">
                <label>Label (FR)</label>
                <input
                  value={formLabelFr}
                  onChange={(e) => setFormLabelFr(e.target.value)}
                />
              </div>
            </div>
            <div className="admin-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddSubmenu}>
                Add Submenu
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "editSectionLabel" && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Section Label</h3>
            <div className="admin-field-row">
              <div className="admin-field">
                <label>Label (EN)</label>
                <input
                  value={formLabelEn}
                  onChange={(e) => setFormLabelEn(e.target.value)}
                />
              </div>
              <div className="admin-field">
                <label>Label (FR)</label>
                <input
                  value={formLabelFr}
                  onChange={(e) => setFormLabelFr(e.target.value)}
                />
              </div>
            </div>
            <div className="admin-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEditSectionLabel}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "editSubmenuLabel" && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Submenu Label: {selectedSubmenu}</h3>
            <div className="admin-field-row">
              <div className="admin-field">
                <label>Label (EN)</label>
                <input
                  value={formLabelEn}
                  onChange={(e) => setFormLabelEn(e.target.value)}
                />
              </div>
              <div className="admin-field">
                <label>Label (FR)</label>
                <input
                  value={formLabelFr}
                  onChange={(e) => setFormLabelFr(e.target.value)}
                />
              </div>
            </div>
            <div className="admin-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEditSubmenuLabel}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "addAddon" && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add-on for {selectedSubmenu}</h3>
            <div className="admin-field-row">
              <div className="admin-field">
                <label>Name (EN)</label>
                <input
                  value={addonName}
                  onChange={(e) => setAddonName(e.target.value)}
                />
              </div>
              <div className="admin-field">
                <label>Name (FR)</label>
                <input
                  value={addonNameFr}
                  onChange={(e) => setAddonNameFr(e.target.value)}
                />
              </div>
            </div>
            <div className="admin-field">
              <label>Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={addonPrice}
                onChange={(e) => setAddonPrice(e.target.value)}
              />
            </div>
            <div className="admin-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddAddon}>
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "addInfo" && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Info Notice for {selectedSubmenu}</h3>
            <div className="admin-field">
              <label>Info Text (EN)</label>
              <textarea
                value={infoText}
                onChange={(e) => setInfoText(e.target.value)}
              />
            </div>
            <div className="admin-field">
              <label>Info Text (FR)</label>
              <textarea
                value={infoTextFr}
                onChange={(e) => setInfoTextFr(e.target.value)}
              />
            </div>
            <p style={{ fontSize: "0.8rem", color: "#6b7280" }}>
              Leave both fields empty to remove the info notice.
            </p>
            <div className="admin-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveInfo}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionsManager;
